<template>
  <div id="objects" class="flex flex-col gap-6 proxima_nova">
    <div class="flex flex-row justify-between items-center">
      <h1 class="text-xl text_color font-semibold">Objects</h1>

      <a-button
        v-if="isDynamicCRMEnabled()"
        @click="createCustomObject"
        class="primary_medium"
      >
        Create Custom Object
      </a-button>
    </div>

    <SkeletonLoader v-if="isLoading" :page="loaderPages.TABLE" />

    <SimpleTable
      v-else
      :headers="objectHeaders"
      :data="entityItems"
      :show-actions-column="true"
      :actions="actionOptions"
      :custom-row="customRow"
      :pagination="null"
      :auto-height="true"
      :scroll="{ x: 600 }"
      identifier-key="id"
    />

    <ConfirmationModal
      v-model:visible="showDeleteModal"
      :title="`Delete ${selectedObject?.name}?`"
      :message="`Are you sure you want to delete the custom object ${selectedObject?.name}? This action cannot be undone and will remove all associated data.`"
      :loading="isDeleting"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
      type="delete"
      confirm-text="Delete"
      loading-text="Deleting..."
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { isDynamicCRMEnabled } from "@/src/utils/dynamicFormUtils.js";
import eventBus from "@/src/utils/eventBus.js";
import CRMService from "@/src/api/CRMService.js";
import SimpleTable from "@/src/components/custom_table_components/SimpleTable.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { CRM_SERVICES, getServiceForSlug } from "@/src/constants/crmConstants.js";

const MANAGED_SERVICES = new Set([
  CRM_SERVICES.FINANCE,
  CRM_SERVICES.INVOICES,
  CRM_SERVICES.PRODUCTS,
  CRM_SERVICES.HR
]);
const isManagedService = (row) => MANAGED_SERVICES.has(row?.service);

const router = useRouter();
const store = useStore();

const showDeleteModal = ref(false);
const selectedObject = ref(null);
const isDeleting = ref(false);

// SimpleTable consumes `{ key, title, width }` shape.
const objectHeaders = [
  { key: "name", title: "Object", width: 320 },
  { key: "service", title: "Service", width: 220 },
];

const entityItems = ref([]);
const isLoading = ref(true);
const fetchDynamicEntities = async () => {
  isLoading.value = true;
  try {
    const response = await CRMService.fetchDynamicCRMListObjects();
    if (response.success && response.objects) {
      const objects = response.objects || [];

      entityItems.value = objects.map((obj, index) => ({
        id: obj.id || index + 1,
        name: obj.plural_name || obj.name,
        entityType: obj.slug,
        service: obj.service || "—",
        isDynamic: true,
        isSystem: obj.is_system || false,
      }));
    }
  } catch (error) {
    console.error("Error fetching dynamic CRM list objects:", error);
    entityItems.value = [];
  } finally {
    isLoading.value = false;
  }
};

const customRow = (record) => {
  let routeConfig;

  if (record.isDynamic) {
    routeConfig = {
      name: "customize-object",
      params: { slug: record.entityType },
      query: {
        tab: "properties",
        ...(record.service && record.service !== "—"
          ? { service: record.service }
          : {}),
      },
    };
  } else {
    routeConfig = {
      path: `/objects/${record.entityType}/properties`,
    };
  }

  const resolvedRoute = router.resolve(routeConfig);

  return {
    onClick: () => router.push(resolvedRoute),
    "data-href": resolvedRoute.href,
  };
};

const createCustomObject = () => {
  router.push({ name: "create-custom-object" });
};

const editObjectProperty = async (row) => {
  let routeConfig;

  if (row.isDynamic) {
    routeConfig = {
      name: "customize-object",
      params: { slug: row.entityType },
      query: {
        tab: "properties",
        ...(row.service && row.service !== "—"
          ? { service: row.service }
          : {}),
      },
    };
  } else {
    routeConfig = {
      path: `/objects/${row.entityType}/properties`,
    };
  }

  await router.push(routeConfig);
};

const deleteObjectProperty = (row) => {
  selectedObject.value = row;
  showDeleteModal.value = true;
};

const confirmDelete = async () => {
  if (!selectedObject.value) return;

  try {
    isDeleting.value = true;
    const service = getServiceForSlug(selectedObject.value.entityType, store);
    const response = await CRMService.deleteCustomObject(
      selectedObject.value.entityType,
      service,
    );

    if (response.success) {
      message.success(
        `${selectedObject.value.name} object deleted successfully!`,
      );
      showDeleteModal.value = false;
      selectedObject.value = null;

      await store.dispatch("objects/refreshObjects");

      eventBus.emit("dynamic-crm-objects-updated");
      await fetchDynamicEntities();
    } else {
      message.error(
        response.error || "Failed to delete custom object. Please try again.",
      );
    }
  } catch (error) {
    console.error("Error deleting custom object:", error);
    message.error(
      error?.response?.data?.error ||
        "Failed to delete custom object. Please try again.",
    );
  } finally {
    isDeleting.value = false;
  }
};

const cancelDelete = () => {
  selectedObject.value = null;
  showDeleteModal.value = false;
};

const actionOptions = computed(() => [
  {
    label: "Edit",
    handler: (row) => editObjectProperty(row),
    icon: imageNames.EDIT,
    condition: (row) => !isManagedService(row),
  },
  {
    label: "Delete",
    handler: (row) => deleteObjectProperty(row),
    icon: imageNames.DELETE_GREY,
    condition: (row) => row.isDynamic && !row.isSystem && !isManagedService(row),
  },
]);

onMounted(async () => {
  await fetchDynamicEntities();
});
</script>
